import { createFileRoute } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, EyeOff, Eye } from "lucide-react";

export const Route = createFileRoute("/admin/anmeldelser")({
  head: () => ({
    meta: [
      { title: "Anmeldelser - AutoXChange Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (<AdminOnlyGuard><AdminAnmeldelser /></AdminOnlyGuard>),
});

type Testimonial = {
  id: string;
  customer_name: string;
  customer_location: string | null;
  quote: string;
  rating: number;
  car_purchased: string | null;
  avatar_url: string | null;
  is_published: boolean;
  display_order: number;
  created_at: string;
};

type FormState = {
  customer_name: string;
  customer_location: string;
  quote: string;
  rating: number;
  car_purchased: string;
  avatar_url: string;
  is_published: boolean;
  display_order: number;
};

const emptyForm: FormState = {
  customer_name: "",
  customer_location: "",
  quote: "",
  rating: 5,
  car_purchased: "",
  avatar_url: "",
  is_published: true,
  display_order: 0,
};

function AdminAnmeldelser() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["admin-testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Testimonial[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_location: form.customer_location.trim() || null,
        quote: form.quote.trim(),
        rating: form.rating,
        car_purchased: form.car_purchased.trim() || null,
        avatar_url: form.avatar_url.trim() || null,
        is_published: form.is_published,
        display_order: form.display_order,
      };
      if (editId) {
        const { error } = await supabase.from("testimonials").update(payload).eq("id", editId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("testimonials").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editId ? "Anmeldelse opdateret" : "Anmeldelse oprettet");
      queryClient.invalidateQueries({ queryKey: ["admin-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["om-os", "testimonials"] });
      setDialogOpen(false);
      setEditId(null);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke gemme"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("testimonials").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Anmeldelse slettet");
      queryClient.invalidateQueries({ queryKey: ["admin-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["om-os", "testimonials"] });
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke slette"),
  });

  const togglePublished = useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      const { error } = await supabase
        .from("testimonials")
        .update({ is_published })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["om-os", "testimonials"] });
    },
  });

  function openCreate() {
    setEditId(null);
    setForm({ ...emptyForm, display_order: (testimonials?.length ?? 0) + 1 });
    setDialogOpen(true);
  }

  function openEdit(t: Testimonial) {
    setEditId(t.id);
    setForm({
      customer_name: t.customer_name,
      customer_location: t.customer_location ?? "",
      quote: t.quote,
      rating: t.rating,
      car_purchased: t.car_purchased ?? "",
      avatar_url: t.avatar_url ?? "",
      is_published: t.is_published,
      display_order: t.display_order,
    });
    setDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kundeanmeldelser</h1>
          <p className="text-sm text-muted-foreground">
            Administrer testimonials der vises på <span className="font-medium">/om-os</span>.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus size={16} className="mr-2" /> Ny anmeldelse
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Indlæser…</p>
      ) : !testimonials || testimonials.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">
            Ingen anmeldelser endnu. Klik &ldquo;Ny anmeldelse&rdquo; for at oprette den første.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((t) => (
            <div
              key={t.id}
              className={`flex flex-col rounded-xl border bg-card p-5 shadow-sm transition ${
                t.is_published ? "border-border" : "border-dashed border-muted-foreground/30 opacity-70"
              }`}
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="flex items-center gap-0.5 text-accent">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} className="fill-current" />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      togglePublished.mutate({ id: t.id, is_published: !t.is_published })
                    }
                    className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title={t.is_published ? "Skjul fra hjemmeside" : "Vis på hjemmeside"}
                  >
                    {t.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Rediger"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(t.id)}
                    className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    title="Slet"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <blockquote className="flex-1 text-sm leading-relaxed text-foreground">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <div className="mt-4 border-t border-border pt-3 text-sm">
                <p className="font-semibold text-foreground">{t.customer_name}</p>
                <p className="text-xs text-muted-foreground">
                  {[t.customer_location, t.car_purchased].filter(Boolean).join(" · ")}
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Rækkefølge: {t.display_order}
                  {!t.is_published && " · Skjult"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Rediger anmeldelse" : "Ny anmeldelse"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="customer_name">Kundens navn *</Label>
              <Input
                id="customer_name"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="fx Mette Sørensen"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="customer_location">By</Label>
                <Input
                  id="customer_location"
                  value={form.customer_location}
                  onChange={(e) => setForm({ ...form, customer_location: e.target.value })}
                  placeholder="København"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="car_purchased">Bil købt</Label>
                <Input
                  id="car_purchased"
                  value={form.car_purchased}
                  onChange={(e) => setForm({ ...form, car_purchased: e.target.value })}
                  placeholder="Audi A4 Avant"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="quote">Anmeldelse *</Label>
              <Textarea
                id="quote"
                rows={4}
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                placeholder="Hvad sagde kunden om jer?"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="rating">Stjerner</Label>
                <select
                  id="rating"
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n} ★
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="display_order">Rækkefølge</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={form.display_order}
                  onChange={(e) =>
                    setForm({ ...form, display_order: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="is_published" className="block">
                  Publiceret
                </Label>
                <div className="flex h-10 items-center">
                  <Switch
                    id="is_published"
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  />
                </div>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="avatar_url">Avatar URL (valgfri)</Label>
              <Input
                id="avatar_url"
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder="https://…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Annuller
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending || !form.customer_name.trim() || !form.quote.trim()
              }
            >
              {saveMutation.isPending ? "Gemmer…" : editId ? "Gem ændringer" : "Opret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet anmeldelse?</AlertDialogTitle>
            <AlertDialogDescription>
              Denne handling kan ikke fortrydes. Anmeldelsen fjernes permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
